import { useStore } from '@nanostores/react';
import { reloadPreview as webcontainerReloadPreview } from '@webcontainer/api';
import { forwardRef, memo, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Resizer from './Resizer';
import { LoadingScreen } from '~/components/workbench/LoadingScreen';
import { chatHooks } from '~/lib/chat-hooks';
import { snapshotManager } from '~/lib/snapshots/snapshot-manager';
import { setPreviewElement } from '~/lib/stores/device-preview';
import { workbenchStore } from '~/lib/stores/workbench';
import { webcontainer } from '~/lib/webcontainer';
import { classNames } from '~/utils/classNames';

interface PreviewProps {
  login?: () => true | Promise<boolean>;
  isResponsiveMode?: boolean;
  showOverlay: boolean;
}

export interface PreviewRef {
  reload(): Promise<void>;
  reloadPreview(): void;
  toggleInspector(): Promise<void>;
  openPreviewInSeparateTab(): Promise<void>;
  navigateTo(pathname: string): void;
}

const FEATURES = [
  'magnetometer',
  'accelerometer',
  'gyroscope',
  'geolocation',
  'microphone',
  'camera',
  'payment',
  'autoplay',
  'serial',
  'xr-spatial-tracking',
  'cross-origin-isolated',
];

enum PreviewLoadState {
  Idle,
  PossiblyWaitingForConnect,
  Ready,
}

const POLICY = ((document as any).featurePolicy?.allowedFeatures() ?? FEATURES).join('; ');

const PreviewImpl = forwardRef<PreviewRef, PreviewProps>(
  ({ login, isResponsiveMode = false, showOverlay = false }, ref) => {
    const framework = useStore(workbenchStore.framework);
    const isReady = useStore(workbenchStore.previewsStore.isReady);
    const previews = useStore(workbenchStore.previews);
    const activePreviewIndex = useStore(workbenchStore.previewsStore.activePreviewIndex);
    const isProcessing = useStore(workbenchStore.isProcessing);
    const firstMessageInHistory = useStore(chatHooks.firstMessageInHistory);
    const userMessageCountInHistory = useStore(chatHooks.userMessageCountInHistory);
    const activePreview = previews[activePreviewIndex];

    const iframeRef = useRef<HTMLIFrameElement>(null);

    const [iframeUrl, setIframeUrl] = useState<string | undefined>();
    const [loadState, setLoadState] = useState(PreviewLoadState.Idle);

    const [mobileWidth, setMobileWidth] = useState(320);
    const isLoadingSnapshot = useStore(snapshotManager.isLoadingSnapshot);

    useEffect(() => {
      if (!activePreview) {
        setIframeUrl(undefined);

        return;
      }

      const { baseUrl } = activePreview;

      setIframeUrl(baseUrl);
    }, [activePreview]);

    useEffect(() => {
      if (loadState !== PreviewLoadState.PossiblyWaitingForConnect) {
        return undefined;
      }

      /**
       * @todo This is just in-case the second event never fires. This can only happen when
       * the SW takes ages to boot up.
       *
       * Once https://github.com/stackblitz/webcontainer/pull/1571 lands we can remove this.
       */
      const timeout = setTimeout(() => setLoadState(PreviewLoadState.Ready), 5000);

      return () => clearTimeout(timeout);
    }, [loadState]);

    useEffect(() => {
      workbenchStore.previewsStore.ref = iframeRef.current;
    });

    useImperativeHandle(ref, () => {
      return {
        async reload() {
          if (iframeRef.current) {
            await webcontainerReloadPreview(iframeRef.current);
          }
        },
        reloadPreview() {
          if (iframeRef.current) {
            webcontainerReloadPreview(iframeRef.current).catch(() => {
              // ignore errors
            });
          }
        },
        async toggleInspector() {
          if (!(await login?.())) {
            return;
          }

          const isInspectorOpen = workbenchStore.isInspectorOpen.get();
          const newValue = !isInspectorOpen;

          workbenchStore.isInspectorOpen.set(newValue);

          import('modern-screenshot/dist/index.mjs?raw').then(async ({ default: modernScreenshotRaw }) => {
            const base64ModernScreenshot = `data:text/javascript;base64,${btoa(modernScreenshotRaw)}`;
            const instance = await webcontainer;

            instance.internal.previewScript.storage.set(
              newValue ? { inspect: newValue, base64ModernScreenshot } : { inspect: newValue },
            );
          });
        },
        async openPreviewInSeparateTab() {
          const url = await workbenchStore.previewsStore.getTunnelURL();

          window.open(url ?? iframeUrl, '_blank');
        },
        navigateTo(pathname: string) {
          const preview = workbenchStore.previews.get()[workbenchStore.previewsStore.activePreviewIndex.get()];

          if (!preview) {
            return;
          }

          let normalized = pathname;

          if (/^(?:www\.|https?:\/\/)/.test(normalized)) {
            normalized = '/';
          } else if (!normalized.startsWith('/')) {
            normalized = `/${normalized}`;
          }

          try {
            const url = new URL(preview.baseUrl + normalized.replace(/^\/+/, '/'));
            setIframeUrl(url.toString());
          } catch {
            setIframeUrl(preview.baseUrl);
          }
        },
      };
    }, [login, iframeUrl]);

    const isStreamingFirstMessage =
      isProcessing && userMessageCountInHistory === 1 && firstMessageInHistory?.role === 'user';

    return (
      <div className="size-full flex flex-col bg-bolt-elements-background-depth-2 dark:bg-bolt-elements-background-depth-1">
        {!activePreview || isLoadingSnapshot || isStreamingFirstMessage ? (
          <LoadingScreen />
        ) : (
          <>
            <div ref={setPreviewElement} className="h-full min-h-0 relative flex">
              {showOverlay && <div className="absolute inset-0 z-max bg-bolt-elements-background-depth-2" />}
              {activePreview ? (
                <>
                  <div
                    className={classNames(
                      'flex gap-2 size-full justify-center items-center bg-bolt-elements-background-depth-2 absolute inset-0',
                      {
                        'z-loading': !isReady,
                        '-z-1': isReady,
                      },
                    )}
                  >
                    <div className="i-svg-spinners:90-ring-with-bg text-bolt-elements-item-contentAccent text-lg" />
                    <div className="text-bolt-elements-textTertiary">
                      {framework?.name === 'expo' ? 'Building the mobile application' : 'Waiting for preview to load'}
                    </div>
                  </div>

                  <Resizer
                    enabled={isResponsiveMode}
                    width={mobileWidth}
                    setWidth={setMobileWidth}
                    isPreviewFullscreen={false}
                  >
                    <iframe
                      ref={iframeRef}
                      className={classNames('border-none size-full', {
                        hidden: loadState !== PreviewLoadState.Ready,
                      })}
                      src={iframeUrl}
                      title="Bolt preview"
                      allow={POLICY}
                      onLoad={() => {
                        setLoadState((loadState) =>
                          loadState === PreviewLoadState.Idle
                            ? PreviewLoadState.PossiblyWaitingForConnect
                            : PreviewLoadState.Ready,
                        );
                      }}
                    />
                  </Resizer>
                </>
              ) : (
                <div className="flex size-full justify-center items-center text-black bg-white">
                  No preview available
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  },
);

export const Preview = memo(PreviewImpl);
